import { OrganizationsService } from "@/client"
import type {
  PercentileColorBand,
  PercentileColorScheme,
} from "@/types/percentileColors"
import {
  DEFAULT_PERCENTILE_COLOR_SCHEME,
  PALETTE,
  resolveColor,
} from "@/utils/percentileColors"
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

const PALETTE_OPTIONS = Object.keys(PALETTE)

interface Props {
  orgId: string
  initialScheme: PercentileColorScheme | null
}

function toEditorBands(scheme: PercentileColorScheme): PercentileColorScheme {
  return scheme.map((b) => ({ ...b }))
}

export function PercentileColorSchemeEditor({ orgId, initialScheme }: Props) {
  const [bands, setBands] = useState<PercentileColorScheme>(() =>
    toEditorBands(initialScheme ?? DEFAULT_PERCENTILE_COLOR_SCHEME),
  )
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { mutate: save, isPending } = useMutation({
    mutationFn: (scheme: PercentileColorScheme) =>
      OrganizationsService.updateOrganizationSettings({
        orgId,
        requestBody: { percentile_color_scheme: scheme },
      }),
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey: ["branding", orgId] })
      queryClient.invalidateQueries({ queryKey: ["organization", orgId] })
    },
    onError: (err: Error) => {
      setError(err.message ?? "Failed to save color scheme")
    },
  })

  function updateMax(index: number, newMax: number) {
    setBands((prev) => {
      const next = prev.map((b) => ({ ...b }))
      next[index].max = newMax
      if (index + 1 < next.length) {
        next[index + 1].min = newMax
      }
      return next
    })
  }

  function updateColor(index: number, color: string) {
    setBands((prev) => prev.map((b, i) => (i === index ? { ...b, color } : b)))
  }

  function addBand() {
    setBands((prev) => {
      if (prev.length === 0) return DEFAULT_PERCENTILE_COLOR_SCHEME
      const last = prev[prev.length - 1]
      if (last.max >= 100) return prev
      const mid = Math.round((last.max + 100) / 2)
      const newBands = prev.map((b) => ({ ...b }))
      newBands[newBands.length - 1].max = mid
      newBands.push({ min: mid, max: 100, color: "gray" })
      return newBands
    })
  }

  function removeBand(index: number) {
    setBands((prev) => {
      if (prev.length <= 1) return prev
      const next = prev.filter((_, i) => i !== index)
      next[0].min = 0
      next[next.length - 1].max = 100
      for (let i = 1; i < next.length; i++) {
        next[i].min = next[i - 1].max
      }
      return next
    })
  }

  function resetToDefault() {
    setBands(toEditorBands(DEFAULT_PERCENTILE_COLOR_SCHEME))
    setError(null)
  }

  return (
    <Stack gap={4}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={2}>
        <Stack gap={1}>
          <Text fontWeight="semibold" fontSize="sm">
            Percentile color scheme
          </Text>
          <Text fontSize="xs" color="fg.muted">
            Colors applied to percentile displays across student-facing views.
            Ranges must cover 0–100.
          </Text>
        </Stack>
        <Button size="sm" variant="outline" onClick={resetToDefault}>
          Reset to default
        </Button>
      </Flex>

      {/* Column headers */}
      <HStack gap={3} px={1}>
        <Text fontSize="xs" color="fg.muted" w="72px" fontWeight="medium">
          FROM
        </Text>
        <Text fontSize="xs" color="fg.muted" w="72px" fontWeight="medium">
          TO
        </Text>
        <Text fontSize="xs" color="fg.muted" flex={1} fontWeight="medium">
          COLOR
        </Text>
        <Box w="28px" />
      </HStack>

      <Stack gap={2}>
        {bands.map((band: PercentileColorBand, i: number) => {
          const isLast = i === bands.length - 1
          const hexColor = resolveColor(band.color)
          const isCustomHex = !PALETTE_OPTIONS.includes(band.color)

          return (
            <HStack
              key={i}
              gap={3}
              px={1}
              py={1}
              borderRadius="md"
              bg="bg.subtle"
            >
              {/* FROM — always locked */}
              <Input
                size="sm"
                w="72px"
                value={band.min}
                readOnly
                disabled
                textAlign="center"
              />
              {/* TO — locked if last band */}
              <Input
                size="sm"
                w="72px"
                type="number"
                value={isLast ? 100 : band.max}
                min={band.min + 1}
                max={99}
                readOnly={isLast}
                disabled={isLast}
                textAlign="center"
                onChange={(e) => updateMax(i, Number(e.target.value))}
              />
              {/* Color selector */}
              <HStack flex={1} gap={2}>
                <Box
                  w="20px"
                  h="20px"
                  borderRadius="sm"
                  flexShrink={0}
                  style={{
                    backgroundColor: hexColor,
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <NativeSelectRoot flex={1} size="sm">
                  <NativeSelectField
                    aria-label={`Color for band ${i + 1}`}
                    value={isCustomHex ? "custom" : band.color}
                    onChange={(e) => {
                      const val = e.target.value
                      if (val !== "custom") updateColor(i, val)
                    }}
                  >
                    {PALETTE_OPTIONS.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                    <option value="custom">Custom hex…</option>
                  </NativeSelectField>
                </NativeSelectRoot>
                {isCustomHex && (
                  <Input
                    size="sm"
                    w="96px"
                    placeholder="#RRGGBB"
                    value={band.color}
                    onChange={(e) => updateColor(i, e.target.value)}
                  />
                )}
              </HStack>
              {/* Delete */}
              <Button
                size="xs"
                variant="ghost"
                w="28px"
                h="28px"
                p={0}
                onClick={() => removeBand(i)}
                disabled={bands.length <= 1}
                aria-label={`Remove band ${i + 1}`}
              >
                ×
              </Button>
            </HStack>
          )
        })}
      </Stack>

      {/* Preview strip */}
      <HStack gap={2} align="center">
        <Text fontSize="xs" color="fg.muted">
          Preview:
        </Text>
        <Flex flex={1} borderRadius="sm" overflow="hidden" h="12px">
          {bands.map((band: PercentileColorBand, i: number) => (
            <Box
              key={i}
              style={{
                flex: band.max - band.min,
                backgroundColor: resolveColor(band.color),
              }}
            />
          ))}
        </Flex>
      </HStack>

      <HStack gap={3}>
        <Button
          size="sm"
          variant="outline"
          onClick={addBand}
          disabled={
            bands.length >= 20 || (bands[bands.length - 1]?.max ?? 0) >= 100
          }
        >
          + Add band
        </Button>
        <Button size="sm" loading={isPending} onClick={() => save(bands)}>
          Save color scheme
        </Button>
      </HStack>

      {error && (
        <Text fontSize="sm" color="red.500">
          {error}
        </Text>
      )}
    </Stack>
  )
}
